import { useState } from 'react';
import { ChevronLeft, ChevronRight, Inbox, Loader2, PhoneCall } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetCallbacksQuery } from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { Callback, CallbackStatus } from '@/types';
import CallbackDrawer from './CallbackDrawer';
import { CALLBACK_STATUSES, STATUS_BADGE, formatAge, formatPlan } from './shared';

const PAGE_SIZE = 20;

type Filter = CallbackStatus | 'all';

function getInitials(firstName?: string, lastName?: string) {
    const initials = `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase();
    return initials || 'A';
}

export default function Callbacks() {
    const currentUser = useAppSelector(selectCurrentUser);
    // `new` is the actionable set and the only one with an SLA feel, so it's the
    // default view rather than the full history.
    const [filter, setFilter] = useState<Filter>('new');
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState<Callback | null>(null);
    const [actionError, setActionError] = useState('');

    const { data, isLoading, isFetching, error } = useGetCallbacksQuery({
        ...(filter === 'all' ? {} : { status: filter }),
        page,
        limit: PAGE_SIZE,
    });

    const items = data?.items ?? [];
    const totalPages = data?.totalPages ?? 1;

    const changeFilter = (next: Filter) => {
        setFilter(next);
        setPage(1);
    };

    // Keep the drawer in step with refetches so a status change is reflected
    // without the row having to be reopened.
    const selectedRow = selected ? (items.find((item) => item.id === selected.id) ?? selected) : null;

    return (
        <div className="flex min-h-dvh flex-col bg-[#EDEFF3]">
            {/* Every section of this navbar has a placeholder default — title
                'Assessment', an assessment blurb, a hardcoded location and a fake
                'John Doe'. Rendering it bare shows all four, so each is turned off
                explicitly and the real user is passed in. The page supplies its own
                heading below. */}
            <YourCheckaNavigation
                showTitle={false}
                showInfo={false}
                showLocation={false}
                notificationCount={0}
                userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Admin'}
                userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'A'}
                only={true}
                className="border-b border-gray-100"
            />

            <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-8">
                <header className="mb-5">
                    <h1 className="text-2xl font-bold text-gray-900">Callback requests</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Leads from the public request form, newest first.
                        {typeof data?.total === 'number' && (
                            <span className="ml-1 font-medium text-gray-700">
                                {data.total} {filter === 'all' ? 'total' : filter}
                            </span>
                        )}
                    </p>
                </header>

                <div className="mb-4 flex flex-wrap gap-2">
                    {(['all', ...CALLBACK_STATUSES] as Filter[]).map((option) => (
                        <button
                            key={option}
                            type="button"
                            onClick={() => changeFilter(option)}
                            className={`rounded-full border px-3.5 py-1.5 text-sm font-medium capitalize transition-colors ${
                                filter === option
                                    ? 'border-[#FF6B35] bg-[#FF6B35] text-white'
                                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                            }`}
                        >
                            {option}
                        </button>
                    ))}
                </div>

                {actionError && (
                    <p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                        {actionError}
                    </p>
                )}

                <div className="flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-hidden="true" />
                        </div>
                    ) : error ? (
                        <p className="px-5 py-16 text-center text-sm text-red-600">
                            {parseErrorMessage(error, "Couldn't load callback requests.")}
                        </p>
                    ) : items.length === 0 ? (
                        <div className="flex flex-col items-center gap-2 px-5 py-16 text-center">
                            <Inbox className="h-8 w-8 text-gray-300" aria-hidden="true" />
                            <p className="text-sm font-medium text-gray-900">Nothing here</p>
                            <p className="text-sm text-gray-500">
                                {filter === 'new' ? 'No new callback requests.' : `No ${filter} requests.`}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop: table. Mobile: the same rows as cards — a
                                6-column table is unusable at phone width. */}
                            <table className="hidden w-full text-left md:table">
                                <thead className="bg-gray-50/60">
                                    <tr className="text-xs tracking-wide text-gray-500 uppercase">
                                        <th className="px-5 py-3 font-medium">Name</th>
                                        <th className="px-5 py-3 font-medium">Phone</th>
                                        <th className="px-5 py-3 font-medium">Email</th>
                                        <th className="px-5 py-3 font-medium">Plan</th>
                                        <th className="px-5 py-3 font-medium">Status</th>
                                        <th className="px-5 py-3 font-medium">Age</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {items.map((item) => (
                                        <tr
                                            key={item.id}
                                            tabIndex={0}
                                            role="button"
                                            onClick={() => setSelected(item)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    setSelected(item);
                                                }
                                            }}
                                            className="cursor-pointer transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                                        >
                                            <td className="px-5 py-3.5 text-sm font-medium text-gray-900">
                                                {item.name}
                                            </td>
                                            <td className="px-5 py-3.5 text-sm text-gray-600">{item.phoneNumber}</td>
                                            <td className="max-w-[220px] truncate px-5 py-3.5 text-sm text-gray-600">
                                                {item.email}
                                            </td>
                                            <td className="px-5 py-3.5 text-sm text-gray-600">
                                                {formatPlan(item.planId)}
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <span
                                                    className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${STATUS_BADGE[item.status]}`}
                                                >
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3.5 text-sm whitespace-nowrap text-gray-500">
                                                {formatAge(item.createdAt)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <ul className="divide-y divide-gray-100 md:hidden">
                                {items.map((item) => (
                                    <li key={item.id}>
                                        <button
                                            type="button"
                                            onClick={() => setSelected(item)}
                                            className="flex w-full flex-col gap-1 px-4 py-3.5 text-left transition-colors hover:bg-gray-50"
                                        >
                                            <div className="flex items-baseline justify-between gap-3">
                                                <span className="truncate text-sm font-medium text-gray-900">
                                                    {item.name}
                                                </span>
                                                <span className="shrink-0 text-xs text-gray-400">
                                                    {formatAge(item.createdAt)}
                                                </span>
                                            </div>
                                            <span className="flex items-center gap-1.5 text-sm text-gray-600">
                                                <PhoneCall className="h-3 w-3 text-gray-400" aria-hidden="true" />
                                                {item.phoneNumber}
                                            </span>
                                            <div className="mt-0.5 flex items-center gap-2">
                                                <span
                                                    className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${STATUS_BADGE[item.status]}`}
                                                >
                                                    {item.status}
                                                </span>
                                                <span className="text-xs text-gray-500">{formatPlan(item.planId)}</span>
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}

                    {totalPages > 1 && (
                        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
                            <span className="text-sm text-gray-500">
                                Page {data?.page ?? page} of {totalPages}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    disabled={page <= 1 || isFetching}
                                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40"
                                >
                                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    disabled={page >= totalPages || isFetching}
                                    onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40"
                                >
                                    Next
                                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <CallbackDrawer
                callback={selectedRow}
                onClose={() => {
                    setSelected(null);
                    setActionError('');
                }}
                onError={setActionError}
            />
        </div>
    );
}
